import React from "react";
import ConnectingState from "./states/ConnectingState";
import LoadingState from "./states/LoadingState";
import RateLimitedState from "./states/RateLimitedState";
import ErrorState from "./states/ErrorState";
import UnverifiedState from "./states/UnverifiedState";
import BirthdayPriorityState from "./states/BirthdayPriorityState";
import ClaimBlockedState from "./states/ClaimBlockedState";
import EnteredEarlyState from "./states/EnteredEarlyState";
import NotStartedHiddenState from "./states/NotStartedHiddenState";
import NotStartedScheduledState from "./states/NotStartedScheduledState";
import GeneratingState from "./states/GeneratingState";
import NoArtOrUnauthorizedState from "./states/NoArtOrUnauthorizedState";
import CompletedState from "./states/CompletedState";
import type { UseAotdRaceResult } from "../hooks/useAotdRace";
import type { UiState } from "../state/types";

/**
 * Thin dispatch from a reduced `UiState` to its matching state component.
 * Lives in its own file so `pages/ArtOfTheDayPage.tsx` stays under the
 * 200-line cap required by the phase-1 acceptance criteria.
 */
export interface UiSwitchProps {
  ui: UiState;
  race: UseAotdRaceResult;
  streakIcon: URL | null;
  isAuthenticated: boolean;
  currentUsername: string | null;
}

export const UiSwitch: React.FC<UiSwitchProps> = ({
  ui,
  race,
  streakIcon,
  isAuthenticated,
  currentUsername,
}) => {
  switch (ui.kind) {
    case "connecting":
      return <ConnectingState />;
    case "loading":
      return <LoadingState />;
    case "rate_limited":
      return <RateLimitedState message={ui.message} onRetry={race.retry} />;
    case "error":
      return <ErrorState kind="error" message={ui.message} onRetry={race.retry} />;
    case "failed":
      return <ErrorState kind="failed" message={ui.message} onRetry={race.retry} />;
    case "unverified":
      return <UnverifiedState />;
    case "birthday_priority":
      return (
        <BirthdayPriorityState
          birthdayUsers={ui.birthdayUsers}
          unlockAt={ui.unlockAt}
          onRetry={race.retry}
        />
      );
    case "claim_blocked":
      return (
        <ClaimBlockedState
          ownerUsername={ui.ownerUsername}
          cardSlug={ui.cardSlug}
          cardName={ui.cardName}
          unlockAt={ui.unlockAt}
          message={ui.message}
          onRetry={race.retry}
        />
      );
    case "entered_early":
      return (
        <EnteredEarlyState
          msEarly={ui.msEarly}
          scheduledTime={ui.scheduledTime}
          clock={race.clock}
          onTap={race.tap}
          tapBusy={race.refreshing}
        />
      );
    case "not_started_hidden":
      return <NotStartedHiddenState emojiPenalty={race.emojiPenalty} />;
    case "not_started_scheduled":
      return (
        <NotStartedScheduledState
          scheduledTime={ui.scheduledTime}
          previousArt={ui.previousArt}
          artUrl={ui.artUrl ?? null}
          streakIcon={streakIcon}
          isAuthenticated={isAuthenticated}
          currentUsername={currentUsername}
          onRetry={race.retry}
          clock={race.clock}
          onTap={race.tap}
          tapBusy={race.refreshing}
          onTriggerWayback={race.wayback.trigger}
          emojiPenalty={race.emojiPenalty}
        />
      );
    case "generating":
      return (
        <GeneratingState
          phase={ui.phase}
          participantCount={ui.participantCount}
          participantEmojis={ui.participantEmojis}
          racers={ui.racers}
          message={ui.message}
          generationStatus={ui.generationStatus}
          stepLabel={ui.stepLabel}
          stepName={ui.stepName}
          stepIndex={ui.stepIndex}
          totalSteps={ui.totalSteps}
          percent={ui.percent}
          stepElapsedMs={race.stepElapsedMs}
          previewB64={ui.previewB64}
          previewIndex={ui.previewIndex}
          emojiPenalty={race.emojiPenalty}
        />
      );
    case "no_art_or_unauthorized":
      return <NoArtOrUnauthorizedState isAuthenticated={ui.isAuthenticated} />;
    case "completed":
      return (
        <CompletedState
          art={ui.art}
          artUrl={ui.artUrl}
          hasPermission={ui.hasPermission}
          isAuthenticated={isAuthenticated}
          currentUsername={currentUsername}
          streakIcon={streakIcon}
          generationElapsedTime={ui.generationElapsedTime}
          onTriggerWayback={race.wayback.trigger}
          quote={race.quote}
          emojis={race.emojis}
        />
      );
    default: {
      const _exhaustive: never = ui;
      void _exhaustive;
      return null;
    }
  }
};

export default UiSwitch;
